import { config } from '@/config/config';
import { getEventRootSegment, isEventRootSegment, type EventRootSegment } from '@/lib/event-url';
import type {
  MyRegistrationPreviewResponse,
  MyRegistrationSession,
  MyRegistrationStartModificationResponse,
  OwnerChangePreview,
  RegistrationDetailViewModel,
  SendBibUpdatePayload,
} from '@/types/my-registration';
import type { Team } from '@/types/teams';

// Clave de sessionStorage para la sesión self-service de "Mi inscripción". Separada de
// `rts_presential_session` (operador presencial) — son flujos auth distintos, no se mezclan.
export const MYREG_STORAGE_KEY = 'rts_myreg_session';
const STORAGE_KEY = MYREG_STORAGE_KEY;

// Custom event para que listeners en el mismo tab reaccionen a login/logout (el evento
// nativo `storage` solo dispara entre tabs).
export const MYREG_SESSION_EVENT = 'rts:myreg-session-changed';

// Marker tab-scoped del flujo "Mi inscripción → Editar". Su presencia indica modo modificación
// en cualquier página `/register/*`. Lo siembra `MiInscripcionClient` al pulsar Editar, lo
// limpia `EditModeBar`/timer expiry/sesión rota.
export const MYREG_MODIFICATION_KEY = 'rts_myreg_modification';

// Marker tab-scoped del flujo post-finalize de modificación. Lo siembra el sidebar al
// encolar la modificación y lo lee `MiInscripcionClient` al volver a /my-registration para
// pintar un overlay de "Procesando…" + polling del status del shadow + refresh del detalle.
// Se borra cuando el detalle queda actualizado o por timeout.
export const MYREG_PROCESSING_KEY = 'rts_myreg_processing';

export interface MyRegistrationProcessingInfo {
  shadowId: string;
  startedAt: string; // ISO — para detectar markers huérfanos y abortar el polling
}

export function readMyRegProcessing(): MyRegistrationProcessingInfo | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(MYREG_PROCESSING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MyRegistrationProcessingInfo>;
    if (!parsed?.shadowId || !parsed?.startedAt) return null;
    return { shadowId: parsed.shadowId, startedAt: parsed.startedAt };
  } catch {
    return null;
  }
}

export function writeMyRegProcessing(shadowId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      MYREG_PROCESSING_KEY,
      JSON.stringify({ shadowId, startedAt: new Date().toISOString() })
    );
  } catch { /* ignore */ }
}

export function clearMyRegProcessing(): void {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(MYREG_PROCESSING_KEY); } catch { /* ignore */ }
}

// Evento custom que dispara el confirm de descartar cambios. Se escucha desde EditModeBar
// (para abrir el dialog) y se emite desde el botón Volver del form + el link "Cancelar
// modificación" del sidebar.
export const MYREG_DISCARD_EVENT = 'rts:myreg-edit-discard-request';

// Tipos de modificación soportados. Pareado con `BEReservationModificationType` en el BKD
// (`Common/1.Entity/Cosmos/BEReservationCosmos.cs`). Cualquier comparación con string literal
// de modificación debe importar este tipo en lugar de re-declarar el union — añadir un nuevo
// tipo (p.ej. `cancel`, `transfer`) será un cambio en este fichero, no en 8+ componentes.
export type ModificationType = 'edit' | 'gift' | 'rate-change' | 'change-owner';

// ¿Es el cambio de titular ENTRANTE? Económicamente es un ALTA nueva (líneas completas, comisiones
// de alta), NO un delta sobre la inscripción original — al revés que edit/gift/rate-change. El
// entrante reusa el marker de modificación solo como plumbing (anonymousId del shadow, barra,
// timer). Centraliza el `=== 'change-owner'` que antes se re-derivaba en FormClient,
// RegistrationSidebar, RegistrationContext y CartClient (cada uno por su cuenta).
export function isChangeOwner(mt: ModificationType | null | undefined): boolean {
  return mt === 'change-owner';
}

// Modo de comisión para `applyCommissions`: `null` (alta — management tipo 2 + platform tipo 1) en
// cambio de titular porque el entrante paga como alta nueva; el propio tipo en edit/gift/rate-change
// (delta — el BE aplica tipo 4/5 según corresponda).
export function resolveCommissionMode(mt: ModificationType | null | undefined): ModificationType | null {
  return isChangeOwner(mt) ? null : (mt ?? null);
}

interface MyRegistrationModificationInfo {
  shadowId: string;
  modificationType: ModificationType;
  // anonymousId del shadow, generado por el BE en start-modification. Lo usa `getAnonymousId`
  // para que todas las llamadas a /reservation resuelvan al shadow mientras el flag esté vivo.
  anonymousId?: string;
  // Solo presente en flujo de canje de regalo (modificationType==='gift'). Lo usa
  // `GiftRedemptionClient` para detectar shadows huérfanos cuando el destinatario vuelve a
  // `/gift/{key}` — si coincide con la giftKey actual, libera el shadow y arranca fresh.
  sourceGiftKey?: string;
  // Solo presente en cambio de titular entrante (modificationType==='change-owner'). Key del
  // magic link, para que el landing/auto-cancel vuelva a `/bibmarket/confirm/{key}`.
  sourceChangeOwnerKey?: string;
  // Referencia del dorsal ("BLS-XXXX") para pintar en la barra superior ("Cambio de titular
  // {ref}") — el entrante anónimo no tiene nombre de participante todavía.
  changeOwnerRef?: string;
  // QuestionIds de recompra obligatoria (change-owner): preseleccionados Y bloqueados (disabled)
  // en el form. Persistido en el marker → se mantiene también al volver a editar desde la cesta.
  changeOwnerLockedQuestionIds?: number[];
  // Solo presente en flujo de cambio de tarifa (modificationType==='rate-change'). FeeId origen
  // de la inscripción, para que EditModeBar/Sidebar puedan mostrar "de X a Y" y para que
  // FormClient construya las refund lines del fee origen sin re-consultar el doc original.
  sourceFeeId?: number;
  // Nombre legible del fee origen (= `data.feeName` del snapshot de my-registration en el
  // momento del start). Lo usa FormClient para pintar la línea negativa con un nombre real
  // en lugar de cadena vacía (la API del shadow no devuelve el nombre del fee origen).
  sourceFeeName?: string;
  // Snapshot del bloque `settings.changeFee.questions` del evento al iniciar rate-change.
  // FormClient lo usa para filtrar preguntas del cuestionario de la tarifa destino:
  //   - `showInTargetFee === false` → ocultar (incluso si no estaba respondida en el origen).
  //   - `refundSourceFee === true`  → reservado para próximas iteraciones (refund automático
  //     del saldo de la pregunta original).
  // Se siembra desde MiInscripcionClient — el FE no consulta el evento desde FormClient.
  changeFeeQuestions?: Array<{
    questionId: number;
    showInTargetFee?: boolean;
    refundSourceFee?: boolean;
  }>;
  // Segmento del árbol de rutas ('site' | 'event') desde el que se inició la modificación.
  // Lo captura `writeMyRegModification` de window.location.pathname — los redirects de recovery
  // (`getModificationLandingPath`) deben volver al MISMO árbol aunque el pathname actual ya no
  // sea fiable (hard refresh fuera del árbol, vuelta de pasarela, etc.).
  segment?: EventRootSegment;
}

// Lee el marker de modificación de sessionStorage. Devuelve null fuera del browser o si no
// hay shadow en curso. Las callers no deben parsear el JSON manualmente — usar este helper.
export function readMyRegModification(): MyRegistrationModificationInfo | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(MYREG_MODIFICATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      shadowId?: string;
      modificationType?: ModificationType;
      anonymousId?: string;
      sourceGiftKey?: string;
      sourceChangeOwnerKey?: string;
      changeOwnerRef?: string;
      changeOwnerLockedQuestionIds?: number[];
      sourceFeeId?: number;
      sourceFeeName?: string;
      changeFeeQuestions?: Array<{ questionId?: number; showInTargetFee?: boolean; refundSourceFee?: boolean }>;
      segment?: string;
    };
    if (!parsed.shadowId) return null;
    return {
      shadowId: parsed.shadowId,
      modificationType: parsed.modificationType ?? 'edit',
      anonymousId: typeof parsed.anonymousId === 'string' ? parsed.anonymousId : undefined,
      sourceGiftKey: typeof parsed.sourceGiftKey === 'string' ? parsed.sourceGiftKey : undefined,
      sourceChangeOwnerKey: typeof parsed.sourceChangeOwnerKey === 'string' ? parsed.sourceChangeOwnerKey : undefined,
      changeOwnerRef: typeof parsed.changeOwnerRef === 'string' ? parsed.changeOwnerRef : undefined,
      changeOwnerLockedQuestionIds: Array.isArray(parsed.changeOwnerLockedQuestionIds)
        ? parsed.changeOwnerLockedQuestionIds.filter((n): n is number => typeof n === 'number')
        : undefined,
      sourceFeeId: typeof parsed.sourceFeeId === 'number' ? parsed.sourceFeeId : undefined,
      sourceFeeName: typeof parsed.sourceFeeName === 'string' ? parsed.sourceFeeName : undefined,
      changeFeeQuestions: Array.isArray(parsed.changeFeeQuestions)
        ? parsed.changeFeeQuestions
            .filter((q): q is { questionId: number; showInTargetFee?: boolean; refundSourceFee?: boolean } => typeof q?.questionId === 'number')
            .map(q => ({ questionId: q.questionId, showInTargetFee: q.showInTargetFee, refundSourceFee: q.refundSourceFee }))
        : undefined,
      segment: typeof parsed.segment === 'string' && isEventRootSegment(parsed.segment) ? parsed.segment : undefined,
    };
  } catch {
    return null;
  }
}

// Helper para persistir el marker. Lo usa GiftRedemptionClient tras start-redeem y queda
// disponible para futuros callers que quieran extender el shape sin riesgo.
export function writeMyRegModification(info: MyRegistrationModificationInfo): void {
  if (typeof window === 'undefined') return;
  try {
    // Captura el árbol de rutas en curso si el caller no lo fija — el marker se escribe siempre
    // desde una página bajo /{lang}/{site|event}/{alias}.
    const segment = info.segment ?? getEventRootSegment(window.location.pathname);
    window.sessionStorage.setItem(MYREG_MODIFICATION_KEY, JSON.stringify({ ...info, segment }));
  } catch { /* ignore */ }
}

// Elimina el marker de modificación. Idempotente.
export function clearMyRegModification(): void {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(MYREG_MODIFICATION_KEY); } catch { /* ignore */ }
}

// Devuelve la URL a la que volver cuando un flujo de modificación se interrumpe (timer expirado,
// sesión perdida, hard refresh, etc.):
//   - canje de regalo → `/gift/{key}` (la landing canónica del canje).
//   - edición desde my-registration → `/my-registration`.
//   - sin marker → null (caller cae a su propio fallback, p.ej. `/register/rates`).
//
// NO limpiamos el marker — `MiInscripcionClient` y `GiftRedemptionClient` lo limpian on-mount
// como parte de su auto-cancel del shadow huérfano. Limpiar aquí provocaba races con redirect
// handlers que también leen el marker (típico: useEffect auto-redirect + modal button).
export function getModificationLandingPath(
  lang: string,
  alias: string,
  // Segmento del árbol desde el que llama el caller — fallback cuando el marker es pre-deploy
  // (sin campo segment). Así el recovery no salta de /event a /site por defecto.
  fallbackSegment: EventRootSegment = 'site',
): string | null {
  const info = readMyRegModification();
  if (!info) return null;
  const segment = info.segment ?? fallbackSegment;
  if (info.modificationType === 'gift' && info.sourceGiftKey) {
    return `/${lang}/${segment}/${alias}/gift/${encodeURIComponent(info.sourceGiftKey)}`;
  }
  if (info.modificationType === 'change-owner' && info.sourceChangeOwnerKey) {
    return `/${lang}/${segment}/${alias}/bibmarket/confirm/${encodeURIComponent(info.sourceChangeOwnerKey)}`;
  }
  return `/${lang}/${segment}/${alias}/my-registration`;
}

export interface MyRegistrationApiError {
  statusCode: number;
  message: string;
  errorId?: string;
}

interface RawApiResponse<T> {
  statusCode?: number;
  message?: string;
  errorId?: string;
  data?: T;
}

interface LookupRawResponse {
  accessToken: string;
  expiresAt: string;
  data: RegistrationDetailViewModel;
}

export interface MyRegistrationLookupResult {
  session: MyRegistrationSession;
  data: RegistrationDetailViewModel;
}

// 2 horas — idle timeout. Sliding: cada acción que use la sesión bumpea `lastActivityAt`.
const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;

function isExpired(expiresAt: string): boolean {
  const ts = Date.parse(expiresAt);
  if (Number.isNaN(ts)) return true;
  return ts <= Date.now();
}

function isIdleExpired(lastActivityAt?: string): boolean {
  if (!lastActivityAt) return true;
  const ts = Date.parse(lastActivityAt);
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts > IDLE_TIMEOUT_MS;
}

function readSession(): MyRegistrationSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MyRegistrationSession;
    if (!parsed?.accessToken || !parsed?.eventId || !parsed?.expiresAt || !parsed?.reservationId) {
      return null;
    }
    if (isExpired(parsed.expiresAt) || isIdleExpired(parsed.lastActivityAt)) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(session: MyRegistrationSession): void {
  if (typeof window === 'undefined') return;
  // Ensure lastActivityAt is set; defaults to now if caller no lo proporciona.
  const withActivity: MyRegistrationSession = {
    ...session,
    lastActivityAt: session.lastActivityAt || new Date().toISOString(),
  };
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(withActivity));
  window.dispatchEvent(new Event(MYREG_SESSION_EVENT));
}

function clearSession(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(STORAGE_KEY);
  // Cierra también el flag de modificación: si el participante cerraba sesión teniendo una
  // edición en curso, quedaría huérfana (el shadow lo limpia el TTL de Cosmos). Además así
  // `getAnonymousId` vuelve a la identidad persistente del usuario en la pestaña.
  clearMyRegModification();
  window.dispatchEvent(new Event(MYREG_SESSION_EVENT));
}

// Bumpea `lastActivityAt` y opcionalmente actualiza el snapshot cacheado de `data`.
// Llamar al montar la pantalla del detalle (reset del idle timer) y tras cada acción
// autenticada (toggle visibility, etc.).
function touchSession(dataPatch?: Partial<RegistrationDetailViewModel>): void {
  if (typeof window === 'undefined') return;
  const current = readSession();
  if (!current) return;
  const next: MyRegistrationSession = {
    ...current,
    lastActivityAt: new Date().toISOString(),
    data: dataPatch ? { ...(current.data ?? ({} as RegistrationDetailViewModel)), ...dataPatch } : current.data,
  };
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event(MYREG_SESSION_EVENT));
}

// Petición autenticada al BE de my-registration. Centraliza el boilerplate que se repetía en
// cada método del service: validación de la sesión local (idle/eventId), headers con el JWT
// en `X-MyReg-Token` (custom header — NO `Authorization: Bearer`, que el middleware JwtBearer
// del BE validaría contra Cosmos y devolvería 401 porque nuestro JWT no se persiste allí),
// `X-API-Key` y el mapeo de errores HTTP/red al shape `MyRegistrationApiError`.
// En éxito devuelve la Response cruda (el caller parsea el body según su tipo).
// NO la usan `lookup` (anónimo, con reCAPTCHA) ni `previewModification` (auth dual con
// anonymousId para el flujo gift) — tienen contratos de auth distintos.
async function myRegFetch(
  lang: string,
  eventId: number,
  subPath: string,
  init?: { method?: 'GET' | 'POST' | 'PATCH'; body?: unknown },
): Promise<{ res: Response; error?: undefined } | { res?: undefined; error: MyRegistrationApiError }> {
  const session = readSession();
  if (!session || session.eventId !== eventId) {
    return { error: { statusCode: 401, message: 'No session' } };
  }
  const path = `/api/site/registration/${encodeURIComponent(lang)}/${encodeURIComponent(String(eventId))}/my-registration/${subPath}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-MyReg-Token': session.accessToken,
  };
  if (config.api?.apiKey) headers['X-API-Key'] = config.api.apiKey;
  try {
    const res = await fetch(`${config.reservation.apiUrl}${path}`, {
      method: init?.method ?? 'POST',
      headers,
      body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
      signal: AbortSignal.timeout(config.reservation.timeout),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as RawApiResponse<unknown>;
      return { error: { statusCode: res.status, message: body.message ?? 'Error', errorId: body.errorId } };
    }
    return { res };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { error: { statusCode: 0, message } };
  }
}

export const MyRegistrationService = {
  getSession: readSession,

  getToken(): string | null {
    return readSession()?.accessToken ?? null;
  },

  // Bumpea el idle timer (lastActivityAt = now) y opcionalmente fusiona un patch sobre el
  // snapshot cacheado de `data`. Cualquier acción autenticada del usuario debe llamar a esto.
  touchSession,

  clearSession,

  // Toggle de "Aparecer en listados públicos". Requiere sesión activa (JWT en sessionStorage,
  // se envía como X-MyReg-Token). El BE actualiza SQL via SP y luego parche puntual en
  // Cosmos sobre /showInList — el resto del doc se preserva.
  async updateShowInList(
    lang: string,
    eventId: number,
    visible: boolean
  ): Promise<{ ok: boolean; error?: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, 'show-in-list', { method: 'PATCH', body: { visible } });
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  // Actualiza la dirección del envío de dorsales (`otherData.sendBibNumber`). El BE ejecuta el SP
  // `usp_update_SendBibNumberAnswer` (devuelve las líneas afectadas) y encola la regeneración del
  // doc consolidado en Cosmos por cada una. El cambio NO es inmediato: el caller debe hacer polling
  // sobre `refresh()` hasta ver `otherData.sendBibNumber` reflejando los nuevos datos.
  async updateSendBibNumber(
    lang: string,
    eventId: number,
    body: SendBibUpdatePayload
  ): Promise<{ ok: boolean; error?: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, 'update-send-bib-number', { method: 'POST', body });
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  // Guarda la respuesta a la pregunta de cancelación del evento. El BE persiste vía
  // `usp_Insert_MultipleAnswer` (qty=1) y encola la regen del doc consolidado. El cambio NO es
  // inmediato: el caller debe hacer polling sobre `refresh()` hasta que `questions[]` contenga la
  // pregunta contestada. No es editable una vez puesta (el BE rechaza re-respuestas con 409).
  async saveCancellationAnswer(
    lang: string,
    eventId: number,
    body: { questionId: number; questionOptionId: number }
  ): Promise<{ ok: boolean; error?: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, 'cancellation-answer', { method: 'POST', body });
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  // Arranca una modificación (edit o gift confirmation). Crea una shadow reservation en el BE
  // (dbsqlcosmosathlete/data) ligada al doc original de inscripción consolidada (high). El FE
  // recibe el reservationId+anonymousId del shadow y los usa en /register/form como si fueran
  // una reserva nueva — el flujo de alta es reutilizado tal cual. Skip de seat-reservation lo
  // hace el BE; aquí no hay nada especial.
  async startModification(
    lang: string,
    eventId: number,
    type: ModificationType,
    // - `targetFeeId`: solo legacy/compat. El flujo actual elige la tarifa destino en
    //   /register/rates → set-rate-change-target.
    // - `feeChangeRequestId`: cuando el rate-change arranca desde una solicitud aceptada
    //   (status=1) del listado de Mi Inscripción. El BE lee `feeTargetId` del doc consolidado
    //   y salta el paso de /rates, fijando `Items[0].FeeId` directamente. Persiste
    //   `FeeChangeRequestId` en el shadow para que Finalize llame al SP con status=2 al
    //   completar.
    opts?: { targetFeeId?: number; feeChangeRequestId?: number }
  ): Promise<{ data?: MyRegistrationStartModificationResponse; error?: MyRegistrationApiError }> {
    const reqBody: { type: string; targetFeeId?: number; feeChangeRequestId?: number } = { type };
    if (type === 'rate-change' && opts?.targetFeeId != null) reqBody.targetFeeId = opts.targetFeeId;
    if (type === 'rate-change' && opts?.feeChangeRequestId != null) reqBody.feeChangeRequestId = opts.feeChangeRequestId;
    const r = await myRegFetch(lang, eventId, 'start-modification', { body: reqBody });
    if (r.error) return { error: r.error };
    const body = (await r.res.json().catch(() => ({}))) as RawApiResponse<MyRegistrationStartModificationResponse>;
    if (!body.data?.reservationId) {
      return { error: { statusCode: r.res.status, message: body.message ?? 'Error', errorId: body.errorId } };
    }
    return { data: body.data };
  },

  // Fija la tarifa destino de un shadow rate-change (placeholder hasta este punto). El FE lo
  // llama tras la selección en `/register/rates`. El BE re-valida settings.changeFee, mismo
  // tipo individual/grupal, etc. — los errores típicos son `rate_change_disabled`,
  // `rate_change_deadline_expired`, `rate_change_group_mismatch`, `same_fee`, `fee_not_found`.
  async setRateChangeTarget(
    lang: string,
    eventId: number,
    shadowId: string,
    targetFeeId: number,
  ): Promise<{ ok: true } | { ok: false; error: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, `modification/${encodeURIComponent(shadowId)}/set-rate-change-target`, { body: { targetFeeId } });
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  // Cancela una solicitud de cambio de tarifa en status 0 (Solicitada). El BE valida que la
  // solicitud pertenece al participante autenticado y está en status 0; cualquier otro status
  // (Aceptada/Finalizada/Rechazada/ya Cancelada) → 422. Tras la llamada el BE actualiza SQL +
  // encola para que el doc consolidado en dbsqlcosmoshigh refleje status=-2. El FE hace
  // polling sobre `/refresh` hasta detectar el cambio.
  async cancelFeeChangeRequest(
    lang: string,
    eventId: number,
    requestId: number,
  ): Promise<{ ok: true } | { ok: false; error: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, `fee-change-request/${encodeURIComponent(String(requestId))}/cancel`);
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  // Solicitud de cambio a una tarifa AGOTADA cuando el organizador tiene
  // `settings.changeFee.feeStatusOptions == 0` (decide caso a caso). El BE registra la solicitud
  // en SQL vía `usp_insert_EventFeeChangeRequest` para que el organizador la apruebe/deniegue
  // desde su Manager. NO crea shadow — el shadow rate-change que esté vivo se cancela en FE
  // tras éxito (el participante vuelve a /my-registration con un banner success).
  async requestFeeChange(
    lang: string,
    eventId: number,
    targetFeeId: number,
  ): Promise<{ ok: true } | { ok: false; error: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, 'request-fee-change', { body: { targetFeeId } });
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  // Solicita el cambio de titular (lado saliente). Dos vías:
  //  - Email (default): `targetEmail` obligatorio — el SP genera la key del magic link y el
  //    job SQL envía las instrucciones a esa persona.
  //  - Marketplace (`marketplace: true`): publicación en la Bolsa de inscripciones — sin
  //    targetEmail; `message` lleva la nota pública opcional del saliente.
  // En ambas el BE registra vía `usp_insert_EventChangeOwner` y encola el SB que regenera
  // `otherData.ownerChange` — el caller hace polling sobre /refresh hasta verlo.
  // `payManaged=true` → el entrante paga en plataforma (requiere canSell); `false` → lo
  // gestionan entre ellos (requiere canGift). La key NUNCA viaja en la respuesta.
  async requestChangeOwner(
    lang: string,
    eventId: number,
    payload: { targetEmail?: string; payManaged: boolean; marketplace?: boolean; message?: string },
  ): Promise<{ ok: true; expirationDate?: string } | { ok: false; error: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, 'request-change-owner', { body: payload });
    if (r.error) return { ok: false, error: r.error };
    const body = await r.res.json().catch(() => ({})) as RawApiResponse<{ expirationDate?: string }>;
    return { ok: true, expirationDate: body.data?.expirationDate };
  },

  // Preview del gasto de gestión por cambio de titular (comisión tipo 6). Solo relevante
  // cuando el cambio se gestiona en plataforma. Devuelve { base, commissionAmount, refund,
  // percent, fixed } para que el wizard muestre el importe y el desglose Importe − Gasto =
  // Recibirás. El BE calcula sobre el saldo de productos + tarifa del consolidado.
  async getOwnerChangePreview(
    lang: string,
    eventId: number,
  ): Promise<{ data?: OwnerChangePreview; error?: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, 'owner-change-preview', { method: 'GET' });
    if (r.error) return { error: r.error };
    const body = (await r.res.json().catch(() => ({}))) as RawApiResponse<OwnerChangePreview>;
    if (!body.data) {
      return { error: { statusCode: r.res.status, message: body.message ?? 'Error', errorId: body.errorId } };
    }
    return { data: body.data };
  },

  // Cancela un cambio de titular en curso. Solo cancelable mientras el entrante no haya
  // confirmado el correo (`targetEmailCheck=false`) ni el proceso esté avanzado
  // (`finished`/`finalizeSelected`). El BE ejecuta `usp_update_EventChangeOwnerCancel`
  // (scope por locator del JWT) + encola la regeneración del doc consolidado — el caller
  // mantiene el popup abierto con polling hasta ver la fila con deleteDate.
  async cancelChangeOwner(
    lang: string,
    eventId: number,
    changeOwnerId: number,
  ): Promise<{ ok: true } | { ok: false; error: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, `change-owner/${encodeURIComponent(String(changeOwnerId))}/cancel`);
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  // Solicita la baja de la inscripción. Escribe un doc `type: "cancellation"` en el container
  // Cosmos `registration/requests` (contrato registration-requests-container.md) — escritura
  // DIRECTA, sin cola SB ni polling: tras el 200 basta un `refresh()` para ver el listado.
  // `message` es OPCIONAL (primer mensaje del chat con el organizador). Errores típicos:
  // `cancellation_already_pending` (ya hay una pendiente), `cancellation_not_enabled`.
  async requestCancellation(
    lang: string,
    eventId: number,
    message?: string,
  ): Promise<{ ok: true } | { ok: false; error: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, 'request-cancellation', { body: { message } });
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  // Añade un mensaje del deportista al chat de una solicitud de baja. Solo permitido si la
  // solicitud sigue `pending`, el organizador no cerró la conversación (`chatClosed`) y el
  // último mensaje NO es suyo (regla de turno — el BE valida, errorIds `chat_closed` /
  // `not_your_turn` / `not_pending`).
  async replyCancellationRequest(
    lang: string,
    eventId: number,
    requestId: string,
    message: string,
  ): Promise<{ ok: true } | { ok: false; error: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, `cancellation-request/${encodeURIComponent(requestId)}/reply`, { body: { message } });
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  // Cancela (retira) una solicitud de baja pendiente. Patch directo en el container requests:
  // status → cancelled + processedAt/processedBy del propio deportista. Solo en `pending`.
  async cancelCancellationRequest(
    lang: string,
    eventId: number,
    requestId: string,
  ): Promise<{ ok: true } | { ok: false; error: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, `cancellation-request/${encodeURIComponent(requestId)}/cancel`);
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  // Calcula server-side el delta de la modificación (lo que el participante tiene que pagar
  // o que se le va a devolver). El BE compara `shadow.Economics` (preguntas editables) contra
  // `original.economic.line[]` y devuelve `{ delta, oldTotal, newTotal, currency }`. Esto es
  // la ÚNICA fuente de verdad — el FE no recalcula, solo renderiza.
  async previewModification(
    lang: string,
    eventId: number,
    shadowId: string,
    // En canje de regalo el destinatario es anónimo (no hay session my-registration) — auth por
    // anonymousId que recibió en start-redeem. El BE valida `shadow.UserKey == anonymousId` +
    // `ModificationType == "gift"` para no abrir bypass del JWT en flujos de edit.
    anonymousId?: string
  ): Promise<{ data?: MyRegistrationPreviewResponse; error?: MyRegistrationApiError }> {
    const session = readSession();
    if ((!session || session.eventId !== eventId) && !anonymousId) {
      return { error: { statusCode: 401, message: 'No session' } };
    }
    const path = `/api/site/registration/${encodeURIComponent(lang)}/${encodeURIComponent(String(eventId))}/my-registration/preview-modification`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (session && session.eventId === eventId) headers['X-MyReg-Token'] = session.accessToken;
    if (config.api?.apiKey) headers['X-API-Key'] = config.api.apiKey;
    try {
      const res = await fetch(`${config.reservation.apiUrl}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ shadowId, anonymousId }),
        signal: AbortSignal.timeout(config.reservation.timeout),
      });
      const body = (await res.json().catch(() => ({}))) as RawApiResponse<MyRegistrationPreviewResponse>;
      if (!res.ok || !body.data) {
        return {
          error: {
            statusCode: res.status,
            message: body.message ?? 'Error',
            errorId: body.errorId,
          },
        };
      }
      return { data: body.data };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error';
      return { error: { statusCode: 0, message } };
    }
  },

  // Descarga el justificante. El BE genera el PDF filtrado por `lineRegistrationId`, lo sube
  // a blob y devuelve la URL. Quien tenga el JWT my-registration de esta reserva puede pedirlo;
  // no es accesible sin token aunque conozcas la URL del endpoint.
  async getProof(
    lang: string,
    eventId: number
  ): Promise<{ data?: { url: string; blobId: string }; error?: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, 'proof', { method: 'GET' });
    if (r.error) return { error: r.error };
    const body = (await r.res.json().catch(() => ({}))) as RawApiResponse<{ url: string; blobId: string }>;
    if (!body.data?.url) return { error: { statusCode: 500, message: 'No URL returned' } };
    return { data: body.data };
  },

  // Genera y devuelve la URL del dorsal virtual del participante en el formato pedido (jpg/pdf).
  // El BE lo crea (vía `VirtualBibNumber`), lo sube a blob y devuelve la URL; el FE la abre con
  // `window.open` para disparar la descarga. Mismo patrón que getProof (JWT en X-MyReg-Token).
  async getVirtualBib(
    lang: string,
    eventId: number,
    format: 'jpg' | 'pdf'
  ): Promise<{ data?: { url: string; blobId: string }; error?: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, `virtual-bib/${format}`, { method: 'GET' });
    if (r.error) return { error: r.error };
    const body = (await r.res.json().catch(() => ({}))) as RawApiResponse<{ url: string; blobId: string }>;
    if (!body.data?.url) return { error: { statusCode: 500, message: 'No URL returned' } };
    return { data: body.data };
  },

  // Re-fetch del detalle usando el JWT (sin pedir locator+email otra vez). Pensado para el
  // flujo post-modificación: tras encolar la modificación y esperar a que Finalize procese,
  // el FE llama a esto para obtener el snapshot actualizado y actualizar el cache de sesión.
  // El JWT no se renueva — la sesión sigue con su TTL/idle originales.
  async refresh(
    lang: string,
    eventId: number
  ): Promise<{ data?: RegistrationDetailViewModel; error?: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, 'refresh', { method: 'GET' });
    if (r.error) return { error: r.error };
    const body = (await r.res.json().catch(() => ({}))) as RawApiResponse<RegistrationDetailViewModel>;
    if (!body.data) {
      return { error: { statusCode: r.res.status, message: body.message ?? 'Error', errorId: body.errorId } };
    }
    // Persiste el snapshot fresco en sessionStorage para que la próxima navegación restaure
    // los datos correctos (sin esto, volver a /my-registration tras un refresh exitoso
    // restauraría el snapshot anterior cacheado). Replace completo, NO partial merge: el VM
    // del BE puede haber eliminado claves (p.ej. opciones de pregunta retiradas) y un spread
    // dejaría datos huérfanos del snapshot anterior. Re-leemos la sesión Y re-validamos el
    // eventId: si la sesión fue sustituida por otra de distinto evento mientras este request
    // estaba en vuelo (p.ej. un polling de 90s que sobrevive a un re-login), NO debemos
    // escribir el snapshot del evento viejo sobre la sesión nueva.
    const session = readSession();
    if (session && session.eventId === eventId) {
      const next: MyRegistrationSession = {
        ...session,
        lastActivityAt: new Date().toISOString(),
        data: body.data,
      };
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event(MYREG_SESSION_EVENT));
    }
    return { data: body.data };
  },

  // Equipo(s) en los que el integrante (autenticado por su JWT) está activo. Usado por
  // el bloque "Tu equipo" de Mi inscripción. La baja del integrante vive aquí, NO en el
  // flujo público de equipos por localizador.
  async getTeams(
    lang: string,
    eventId: number
  ): Promise<{ data?: Team[]; error?: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, 'teams', { method: 'GET' });
    if (r.error) return { error: r.error };
    const body = (await r.res.json().catch(() => ({}))) as RawApiResponse<unknown> & { items?: Team[] };
    return { data: body.items ?? [] };
  },

  // Baja del integrante de un equipo (EventoEquipoComponenteEliminaUsuario, Estado -2).
  // El BKD valida server-side que el lineId del JWT está activo en ese equipo.
  async leaveTeam(
    lang: string,
    eventId: number,
    teamId: number
  ): Promise<{ ok: true } | { ok: false; error: MyRegistrationApiError }> {
    const r = await myRegFetch(lang, eventId, `teams/${encodeURIComponent(String(teamId))}/leave`);
    return r.error ? { ok: false, error: r.error } : { ok: true };
  },

  async lookup(
    lang: string,
    eventId: number,
    locator: string,
    email: string,
    recaptchaToken?: string
  ): Promise<{ data?: MyRegistrationLookupResult; error?: MyRegistrationApiError }> {
    const path = `/api/site/registration/${encodeURIComponent(lang)}/${encodeURIComponent(String(eventId))}/my-registration/lookup`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (config.api?.apiKey) headers['X-API-Key'] = config.api.apiKey;

    try {
      const res = await fetch(`${config.reservation.apiUrl}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ locator, email, recaptchaToken }),
        signal: AbortSignal.timeout(config.reservation.timeout),
      });
      const body = (await res.json().catch(() => ({}))) as RawApiResponse<LookupRawResponse>;
      if (!res.ok) {
        return {
          error: {
            statusCode: res.status,
            message: body.message ?? 'Error',
            errorId: body.errorId,
          },
        };
      }
      const payload = body.data;
      if (!payload?.accessToken || !payload?.data?.reservationId) {
        return { error: { statusCode: 500, message: 'Invalid response' } };
      }
      const session: MyRegistrationSession = {
        accessToken: payload.accessToken,
        expiresAt: payload.expiresAt,
        lastActivityAt: new Date().toISOString(),
        eventId,
        reservationId: payload.data.reservationId,
        locator: payload.data.locator ?? locator,
        // Snapshot del detalle — al recargar/navegar usaremos esto para restaurar la vista
        // sin pedir locator+email otra vez.
        data: payload.data,
      };
      writeSession(session);
      return { data: { session, data: payload.data } };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error';
      return { error: { statusCode: 0, message } };
    }
  },
};

export type MyRegistrationApi = typeof MyRegistrationService;
